import { useConnectionAction } from "./useConnectionAction";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@components/ui/button";
import { Label } from "@components/ui/label";
import { FormChoiceSelect } from "@components/ui/choice-select";
import { startSalesforceOAuth, updateIntegrationConnection, type IntegrationConnection } from "../../lib/integrations";
import { FormField } from "./FormField";
import { salesforceFormSchema, type SalesforceForm } from "./integrationFormSchemas";

export function SalesforceConnectionCard({ connection, onSaved, onCancel }: { connection?: IntegrationConnection; onSaved: () => Promise<void>; onCancel: () => void }) {
  const action = useConnectionAction();
  const isSubmitting = action.pending;
  const { register, control, handleSubmit, formState: { errors } } = useForm<SalesforceForm>({
    resolver: zodResolver(salesforceFormSchema),
    defaultValues: {
      name: connection?.name ?? "Salesforce",
      environment: (connection?.configSummary.environment as SalesforceForm["environment"] | undefined) ?? "production",
    },
  });

  const authorize = async (values: SalesforceForm) => {
    const { authorizationUrl } = await startSalesforceOAuth({ ...values, ...(connection ? { connectionId: connection.id } : {}) });
    action.redirect(authorizationUrl);
  };

  const save = handleSubmit((values) => action.run(connection ? "save" : "authorize", async () => {
    try {
      if (!connection) return await authorize(values);
      await updateIntegrationConnection(connection.id, { name: values.name, config: { environment: values.environment } });
      toast.success("Salesforce settings updated.");
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to connect Salesforce.");
    }
  }));

  const reconnect = handleSubmit((values) => action.run("authorize", async () => {
    try { await authorize(values); }
    catch (error) { toast.error(error instanceof Error ? error.message : "Unable to authorize connection."); }
  }));

  return (
    <form className="space-y-4" onSubmit={save} noValidate>
      <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
        Salesforce is read-only in this first slice. IO Spoke imports bounded opportunity context without changing CRM records.
      </div>
      <FormField id="salesforce-name" label="Connection name" registration={register("name")} error={errors.name} />
      <div className="space-y-2">
        <Label htmlFor="salesforce-environment">Salesforce environment</Label>
        <FormChoiceSelect id="salesforce-environment" control={control} name="environment" options={[{ value: "production", label: "Production" }, { value: "sandbox", label: "Sandbox" }]} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={isSubmitting} loading={connection ? action.saving : action.authorizing} loadingLabel={connection ? "Saving…" : "Authorizing…"}>
          {connection ? "Save settings" : <> <ExternalLink className="mr-2 h-4 w-4" />Continue to Salesforce</>}
        </Button>
        {connection ? (
          <Button type="button" variant="outline" onClick={reconnect} disabled={isSubmitting} loading={action.authorizing} loadingLabel="Authorizing…">
            Reconnect Salesforce
          </Button>
        ) : null}
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
